import { Box } from '@mui/material';
import { Currency } from '@synergy/models';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { ReactElement, ReactNode } from 'react';
import { tokens } from '@synergy/theme-palo';

import { CustomerBonusSettlementDetails } from '../../models/customer-bonus-settlement-details';

const c = tokens.color;

const Line = ({
    label,
    value,
    currency,
    strong,
    rule,
}: {
    label: ReactNode;
    value: number;
    currency: Currency;
    strong?: boolean;
    rule?: 'soft' | 'hard';
}): ReactElement => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '10px',
            padding: '5px 0',
            fontSize: '12.5px',
            fontWeight: strong ? 650 : 400,
            color: strong ? c.ink : c.ink2,
            borderTop: rule ? `1px solid ${'hard' === rule ? c.lineHard : c.lineSoft}` : undefined,
        }}
    >
        <Box>{label}</Box>
        <Box sx={{ fontVariantNumeric: 'tabular-nums', color: c.ink, whiteSpace: 'nowrap' }}>
            <FormattedNumber maximumFractionDigits={2} style="currency" currency={currency} value={value} />
        </Box>
    </Box>
);

/**
 * The settlement's totals, as the sidebar showed them. The dock around this
 * carries the heading now, so the panel no longer brings a dark bar of its own.
 */
export const PaloBonusSettlementDetailDock = ({
    currency,
    bonusSettlement,
}: {
    currency: Currency;
    bonusSettlement: CustomerBonusSettlementDetails;
}): ReactElement => (
    <Box>
        <Box
            sx={{
                fontSize: '9.5px',
                fontWeight: 600,
                letterSpacing: '.08em',
                textTransform: 'uppercase',
                color: c.ink3,
                marginBottom: '6px',
            }}
        >
            <FormattedMessage id="app.common.overallOverview" />
        </Box>

        <Line
            label={<FormattedMessage id="app.bonusSettlements.detail.mode.percentage" />}
            value={bonusSettlement?.totalByMode.percentage ?? 0}
            currency={currency}
        />
        <Line
            label={<FormattedMessage id="app.bonusSettlements.detail.mode.revenueAmount" />}
            value={bonusSettlement?.totalRevenue ?? 0}
            currency={currency}
        />
        <Line
            label={<FormattedMessage id="app.bonusSettlements.detail.memberBonusPayout" />}
            value={bonusSettlement.totalMemberBonus}
            currency={currency}
            strong={true}
            rule="hard"
        />
    </Box>
);
